class ComponentScroll extends Theme.Component {
  #state = "idle";
  #drag_start = 0;
  #drag_threshold = 10;
  #drag_delta = 0;
  #scroll_start = 0;
  #animation_frame_id = null;
  #snap_points = [];

  connectedCallback() {
    this.setStyles();
    this.setSnapPoints();

    this.addDirectEvent("resize", window, Theme.Helpers.debounce(this.onResize));
    this.addDirectEvent("pointerdown", this, this.onPointerDown);
    this.addDirectEvent("pointermove", this, this.onPointerMove);
    this.addDirectEvent("pointerup", this, this.onPointerUp);

    this.addDirectEvent("dragstart", this, (event) => {
      event.preventDefault()
    });

    this.addDirectEvent("collectionslidernext:click", document, this.nextGroup);
    this.addDirectEvent("collectionsliderprevious:click", document, this.previousGroup);
  }

  setStyles() {
    if (this.isScrollable()) {
      this.classList.add("state--scrollable");
      this.updateScrollbarCompensation();
    } else {
      this.classList.remove("state--scrollable");
    }
  }

  updateScrollbarCompensation() {
    const scrollbar_width = window.innerWidth - document.documentElement.clientWidth;

    if (scrollbar_width > 0) {
      this.style.setProperty("--scrollbar-compensation", `${scrollbar_width / 2}px`);
    } else {
      this.style.removeProperty("--scrollbar-compensation");
    }
  }

  setSnapPoints() {
    const container_box = this.getBoundingClientRect();
    const container_style = getComputedStyle(this);
    const container_padding_left = parseFloat(container_style.paddingLeft);
    const container_edge = container_box.left + container_padding_left;
    const container_scroll = this.scrollLeft;

    const max = this.scrollWidth - this.clientWidth;

    const points = [];

    this.elements.items.forEach((element) => {
      const element_box = element.getBoundingClientRect();
      const item_scroll = element_box.left - container_edge;
      const distance = container_scroll + item_scroll;
      const clamped = Math.max(0, Math.min(distance, max));
      const rounded = Math.round(clamped);

      if (!points.includes(rounded)) {
        points.push(rounded);
      }
    });

    this.#snap_points = points;
  }

  isScrollable() {
    return (this.scrollWidth - this.clientWidth) > 1;
  }

  onResize() {
    this.setStyles();
    this.setSnapPoints();
  }

  onPointerDown(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (event.button !== 0) {
      return;
    }

    this.#state = "tracking";
    this.#scroll_start = this.scrollLeft;
    this.#drag_start = event.pageX;
  }

  onPointerMove(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    if (this.#state === "idle") {
      return;
    }

    this.#drag_delta = event.pageX - this.#drag_start;

    if (this.isDrag() && this.#state === "tracking") {
      this.setPointerCapture(event.pointerId);
      this.#state = "dragging";
      this.classList.add("state--dragging");
      this.startAnimationLoop();
    }
  }

  onPointerUp(event) {
    if (event.pointerType === "touch") {
      this.reset();
      return;
    }

    const distance = Math.abs(this.scrollLeft - this.#scroll_start);

    if (this.#state === "dragging" && event.pointerType === "mouse") {
      this.preventDefaultClick();
    }

    if (this.#state === "dragging" || distance >= this.#drag_threshold) {
      this.animateScrollTo(this.getDirectionalSnapPoint());
    }

    this.reset();
  }

  isDrag() {
    const threshold = 10;
    const distance = Math.abs(this.#drag_delta);

    return distance >= threshold;
  }

  startAnimationLoop() {
    if (this.#animation_frame_id) {
      return;
    }

    const loop = () => {
      this.scrollLeft = this.#scroll_start - this.#drag_delta;
      this.#animation_frame_id = requestAnimationFrame(loop);
    };

    this.#animation_frame_id = requestAnimationFrame(loop);
  }

  stopAnimationLoop() {
    cancelAnimationFrame(this.#animation_frame_id);
    this.#animation_frame_id = null;
  }

  preventDefaultClick() {
    this.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    }, {
      capture: true,
      once: true
    });
  }

  animateScrollTo(value) {
    this.scrollTo({
      left: value,
      behavior: "smooth"
    });
  }

  getDirectionalSnapPoint() {
    const container_scroll = this.scrollLeft;

    if (!this.#snap_points.length) {
      return container_scroll;
    }

    const direction = Math.sign(container_scroll - this.#scroll_start);

    if (direction > 0) {
      return this.getNextSnapPoint();
    }

    if (direction < 0) {
      return this.getPreviousSnapPoint();
    }

    return container_scroll;
  }

  getNextSnapPoint() {
    const points = this.#snap_points;
    const next = points.find((p) => p > this.scrollLeft);

    return next ?? points.at(-1);
  }

  getPreviousSnapPoint() {
    const points = this.#snap_points;
    const previous = points.toReversed().find((p) => p < this.scrollLeft);

    return previous ?? points[0];
  }

  nextItem() {
    this.animateScrollTo(this.getNextSnapPoint());
  }

  previousItem() {
    this.animateScrollTo(this.getPreviousSnapPoint());
  }

  nextGroup() {
    const group_size = this.getGroupSize();
    const tolerance = 0.5;

    const current_index = this.#snap_points.findIndex((point) => {
      return point >= (this.scrollLeft - tolerance);
    });

    const index = Math.min(current_index + group_size, this.#snap_points.length - 1);

    this.animateScrollTo(this.#snap_points[index]);
  }

  previousGroup() {
    const group_size = this.getGroupSize();
    const tolerance = 0.5;

    const current_index = this.#snap_points.findIndex((point) => {
      return point >= (this.scrollLeft - tolerance);
    });

    const index = Math.max(current_index - group_size, 0);

    this.animateScrollTo(this.#snap_points[index]);
  }

  getGroupSize() {
    const container_width = this.clientWidth;
    const item_width = this.elements.items[0].clientWidth;

    return Math.floor(container_width / item_width);
  }

  reset() {
    this.stopAnimationLoop();
    this.#state = "idle";
    this.#drag_start = 0;
    this.#scroll_start = 0;
    this.classList.remove("state--dragging");
  }
}

customElements.define("component-scroll", ComponentScroll);
